// Parse "Ground Transportation Schedule" PDFs (jetBlue / Endeavor /
// Horizon / PSA — same template) into the same row shape the xls importer
// emits, so downstream code in ImportData.js stays identical.
//
// Strategy:
//   1. Apply each page's viewport matrix to text items so coords run
//      top-left → bottom-right (handles rotated PDFs — these are authored
//      at 90°).
//   2. Cluster items by Y to form "physical lines".
//   3. Find the table header pair (DATE/DEPARTMENT/#RIDERS over
//      Location/From/Pickup Date/Time/Location/To) to anchor columns.
//   4. Detect data rows by content (a row that has a #riders integer + a
//      "DD MON HH:MM" pickup time + an airport code).
//   5. Merge multi-line hotel-name wraps from the adjacent
//      "Flight (N) / InFlight (M)" department-header row above and the
//      pure-text continuation row below.
import * as pdfjs from "pdfjs-dist/legacy/build/pdf";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import {
  easternLocalToUtc,
  endsWithTime,
  getAirlineFromEntry,
  getDropoffTimeFromEntry,
  getFlightNoFromEntry,
  passValidation,
} from "../Utils/import";
import { airportLocalToUtc } from "../../utils/airportTz";

// Worker file is copied into act-manager/public/ at build time so it's
// served from the same origin (avoids CORS + version-mismatch CDN issues).
pdfjs.GlobalWorkerOptions.workerSrc =
  `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

const Y_TOL = 3;
const MONTH3 = "(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)";
const MONTHS_LONG = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const MONTH3_LIST = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DATE_RE = new RegExp(`^\\d{1,2}-${MONTH3}-\\d{4}$`, "i");
const TIME_RE = new RegExp(`^\\d{1,2}\\s${MONTH3}\\s\\d{1,2}:\\d{2}$`, "i");
const AIRPORT_RE = /^[A-Z]{3}$/;
// GoJet calendar bits:
const GOJET_MONTH_RE = new RegExp(`\\b(${MONTHS_LONG.join("|")})\\s+(\\d{4})\\b`);
const GOJET_HEADER_RE = /^([A-Z]{3})\s*\|\s*(.+?)\s*\|\s*\d+$/;
const GOJET_LINE_RE = /^(\d{1,2}):(\d{2})\s+(\S+)\s+\((\d+)\)(?:\s+\(([A-Z]{2,3})\))?$/;
const GOJET_DAY_RE = /^\d{1,2}$/;
// Condor RP-ReportPortal "Overnight list" bits:
const CONDOR_DATE_RE = /^(\d{2})\.(\d{2})\.(\d{2})$/;
const CONDOR_HHMM_RE = /^\d{1,2}:\d{2}$/;
const CONDOR_FLIGHT_RE = /^([A-Z0-9]{2,3})\s+(\S+)$/;
// Condor's PDF lists overnight pairings but never names the hotel — these
// are the hotels Condor crews actually use per station.
const CONDOR_HOTELS = {
  LAS: "Sahara Las Vegas",
  LAX: "Hilton Culver City",
};

function rowsOf(items) {
  if (!items.length) return [];
  const sorted = [...items].sort((a, b) => a.y - b.y);
  const rows = [];
  let cur = [sorted[0]];
  for (let k = 1; k < sorted.length; k++) {
    if (Math.abs(sorted[k].y - cur[0].y) <= Y_TOL) cur.push(sorted[k]);
    else { rows.push(cur); cur = [sorted[k]]; }
  }
  rows.push(cur);
  return rows.map((row) => row.sort((a, b) => a.x - b.x));
}

function findColumns(rows) {
  for (let i = 0; i < rows.length - 1; i++) {
    const top = rows[i];
    const bot = rows[i + 1];
    const topJoined = top.map((r) => r.str).join(" ");
    const botJoined = bot.map((r) => r.str).join(" ");
    if (
      /\bDATE\b/i.test(topJoined) && /DEPARTMENT/i.test(topJoined) && /#RIDERS/i.test(topJoined) &&
      /Location/.test(botJoined) && /\bFrom\b/.test(botJoined) &&
      /Pickup Date\/Time/.test(botJoined) && /\bTo\b/.test(botJoined)
    ) {
      const cols = {};
      let locSeen = 0;
      for (const item of bot) {
        if (item.str === "Location") {
          if (locSeen === 0) cols.airport1 = item.x;
          else cols.airport2 = item.x;
          locSeen++;
        }
        if (item.str === "From") cols.from = item.x;
        if (item.str === "Pickup Date/Time") cols.pickupTime = item.x;
        if (item.str === "To") cols.to = item.x;
      }
      return cols;
    }
  }
  return null;
}

function parseDataRow(items) {
  const ridersIdx = items.findIndex((i) => /^\d+$/.test(i.str));
  if (ridersIdx === -1) return null;
  const timeIdx = items.findIndex((i) => TIME_RE.test(i.str));
  if (timeIdx === -1) return null;

  let airport1Idx = -1;
  for (let k = ridersIdx + 1; k < timeIdx; k++) {
    if (AIRPORT_RE.test(items[k].str)) { airport1Idx = k; break; }
  }
  if (airport1Idx === -1) return null;

  let airport2Idx = -1;
  for (let k = timeIdx + 1; k < items.length; k++) {
    if (AIRPORT_RE.test(items[k].str)) { airport2Idx = k; break; }
  }

  const dateItem = items[0];
  const date = DATE_RE.test(dateItem.str) ? dateItem.str : null;
  const from = items.slice(airport1Idx + 1, timeIdx).map((i) => i.str).join(" ").trim();
  const to = airport2Idx !== -1
    ? items.slice(airport2Idx + 1).map((i) => i.str).join(" ").trim()
    : "";

  return {
    date,
    passengers: items[ridersIdx].str,
    airport: items[airport1Idx].str,
    // Also emit the second Location column when it differs from the first —
    // used to detect cross-station shuttles (e.g. JB MCO PDFs where crew is
    // driven MCO→FLL for their next flight).
    toAirport: airport2Idx !== -1 ? items[airport2Idx].str : null,
    from,
    pickupTime: items[timeIdx].str,
    to,
  };
}

function continuationOnly(items) {
  if (!items.length) return false;
  for (const i of items) {
    if (/^\d+$/.test(i.str)) return false;
    if (TIME_RE.test(i.str)) return false;
    if (DATE_RE.test(i.str)) return false;
  }
  return true;
}

function isDepartmentHeader(items) {
  return items.some((i) => /^Flight\s*\(\d+\)/.test(i.str));
}

function isDeptOrInflightToken(str) {
  return /^Flight\s*\(\d+\)/.test(str) || /^InFlight/.test(str) ||
         str === "/" || /^\(\d+\)$/.test(str);
}

function parsePage(items, state) {
  const rows = rowsOf(items);
  const cols = findColumns(rows);
  if (!cols) return [];

  const parsedRows = rows.map((row) => parseDataRow(row));

  // Anchor From/To column on observed data positions (headers and data sit
  // at different X — usually data is ~50 px left of header).
  const fromXs = [];
  const toXs = [];
  for (let i = 0; i < rows.length; i++) {
    if (!parsedRows[i]) continue;
    const row = rows[i];
    const ridersIdx = row.findIndex((it) => /^\d+$/.test(it.str));
    const timeIdx = row.findIndex((it) => TIME_RE.test(it.str));
    let a1 = -1, a2 = -1;
    for (let k = ridersIdx + 1; k < timeIdx; k++) if (AIRPORT_RE.test(row[k].str)) { a1 = k; break; }
    for (let k = timeIdx + 1; k < row.length; k++)  if (AIRPORT_RE.test(row[k].str)) { a2 = k; break; }
    if (a1 !== -1 && a1 + 1 < timeIdx) fromXs.push(row[a1 + 1].x);
    if (a2 !== -1 && a2 + 1 < row.length) toXs.push(row[a2 + 1].x);
  }
  const median = (arr) =>
    arr.length ? [...arr].sort((a, b) => a - b)[Math.floor(arr.length / 2)] : null;
  const fromAnchor = median(fromXs) ?? cols.from;
  const toAnchor = median(toXs) ?? cols.to;
  const leftLimit = fromAnchor - 80;

  // Carry the most recent DATE token forward across the whole DOCUMENT
  // (state persists across pages). Multi-page PDFs only print the DATE on
  // the first row of each day; subsequent pages can have data rows that
  // continue from the prior page's date, so the carry-forward needs to
  // outlive a single page.
  for (const row of rows) {
    if (row[0] && DATE_RE.test(row[0].str)) state.curDate = row[0].str;
    const idx = rows.indexOf(row);
    if (parsedRows[idx]) parsedRows[idx].date = parsedRows[idx].date || state.curDate;
  }

  // Attach each non-data row's wrap items to its NEAREST data row by Y. This
  // unified rule handles all the wrap patterns observed across PDFs:
  //   - JB LAX: "MDR Marina..." on a department-header row above the data,
  //     plus "Hotel" continuation line below → both attach to the row
  //     they're adjacent to.
  //   - Horizon LAX: "Ayres Hotel..." on a bare continuation line above the
  //     data (no department-header preamble) → attaches to the data row
  //     immediately below it.
  //   - Endeavor / PSA: similar "Holiday Inn Express..." above each data
  //     row that needs it.
  // Department headers ("Flight (N) / InFlight (M)") are stripped item-by-item
  // inside addItem so only their hotel-wrap portion gets merged.
  const dataIdxs = [];
  for (let i = 0; i < rows.length; i++) if (parsedRows[i]) dataIdxs.push(i);
  const dataYs = dataIdxs.map((i) => rows[i][0].y);

  // Reject tokens that belong to the table chrome (column headers, page
  // footers, section banners) so they don't get merged as wrap content.
  const NOISE_EXACT = new Set([
    "DATE", "DEPARTMENT", "#RIDERS", "PICK UP", "DROP OFF",
    "Location", "From", "To", "Pickup Date/Time",
  ]);
  const NOISE_RE = /^(Page \d+ of \d+|Created \d|API FAX|Updated Schedules|Copyright)/i;
  const rightLimit = toAnchor + 80;

  const addItemTo = (rec, it) => {
    if (it.x < leftLimit || it.x > rightLimit) return;
    if (isDeptOrInflightToken(it.str)) return;
    if (DATE_RE.test(it.str)) return;
    if (NOISE_EXACT.has(it.str)) return;
    if (NOISE_RE.test(it.str)) return;
    const dFrom = Math.abs(it.x - fromAnchor);
    const dTo = Math.abs(it.x - toAnchor);
    if (dTo < dFrom) rec.to = `${rec.to} ${it.str}`.trim();
    else             rec.from = `${rec.from} ${it.str}`.trim();
  };

  // Wrap-to-data Y delta is ~4 px; reject anything past ~25 px so page
  // footers / watermarks at the bottom margin don't get pulled in as a
  // false "To" continuation on the last data row of the page.
  const MAX_WRAP_DELTA = 25;

  for (let i = 0; i < rows.length; i++) {
    if (parsedRows[i]) continue;
    const row = rows[i];
    // Find nearest data row by Y.
    const y = row[0].y;
    let bestIdx = -1, bestD = Infinity;
    for (let k = 0; k < dataIdxs.length; k++) {
      const d = Math.abs(dataYs[k] - y);
      if (d < bestD) { bestD = d; bestIdx = dataIdxs[k]; }
    }
    if (bestIdx === -1 || bestD > MAX_WRAP_DELTA) continue;
    // Department headers always belong to the NEXT data row (the one below
    // them); enforce that here even if nearest by Y happens to be above.
    if (isDepartmentHeader(row)) {
      const below = dataIdxs.find((di) => di > i);
      if (below != null) bestIdx = below;
    }
    const rec = parsedRows[bestIdx];
    if (!rec) continue;
    for (const it of row) addItemTo(rec, it);
  }

  return parsedRows.filter(Boolean);
}

function detectFormat(items) {
  // GoJet calendar PDFs have a weekday header row and a "Pick-up from Airport"
  // marker. Standard "Ground Transportation Schedule" PDFs have "DATE",
  // "DEPARTMENT", "#RIDERS" labels. Condor RP-ReportPortal "Overnight list"
  // PDFs have those literal title words.
  const text = items.map((i) => i.str).join(" ");
  if (/RP-ReportPortal/.test(text) && /Overnight list/.test(text)) return "condor";
  if (/Pick-up from Airport/.test(text) && /Sunday|Monday|Tuesday/.test(text)) return "gojet";
  if (/#RIDERS/.test(text) && /Pickup Date\/Time/.test(text)) return "standard";
  return null;
}

// Detect which airline OWNS the schedule sheet from the page header. API
// (Accommodations Plus) prints one file per airline and mixes flight prefixes
// inside it — a Horizon file lists both "DHA 0063" (Horizon system) and
// "AS 2328" (Alaska codeshare) rows, but every row is billable to Horizon.
// So the sheet header, not the row prefix, decides the client airline.
// Returns {code, force}: `force=true` overrides the row's own client (used
// for mixed-prefix Horizon sheets); `force=false` only fills in when the row
// doesn't have its own prefix (e.g. a hotel-to-hotel positioning row where
// neither side is a flight string).
function detectSourceAirline(items) {
  const text = items.map((i) => i.str).join(" ");
  // API/APH publishes one file per client airline; flight prefixes inside
  // can be codeshares, regional operators, or subsidiaries (Horizon DHA/AS,
  // Air Canada AC/ZX/RV, JetBlue with codeshares). Force each row's client
  // to the sheet owner so billing rolls up correctly and duplicate airline
  // records like ZX/AC aren't created.
  if (/Horizon Air Ground Transportation Schedule/i.test(text)) return { code: "QX", force: true };
  if (/Air Canada Ground Transportation Schedule/i.test(text)) return { code: "AC", force: true };
  if (/Southwest Airlines Ground Transportation Schedule/i.test(text)) return { code: "WN", force: true };
  if (/jetBlue Ground Transportation Schedule/i.test(text)) return { code: "B6", force: true };
  if (/PSA Airlines Ground Transportation Schedule/i.test(text)) return { code: "OH", force: true };
  if (/Endeavor Air Ground Transportation Schedule/i.test(text)) return { code: "9E", force: true };
  if (/Republic Airways Ground Transportation Schedule/i.test(text)) return { code: "YX", force: true };
  if (/Envoy.*Ground Transportation Schedule/i.test(text)) return { code: "MQ", force: true };
  if (/SkyWest.*Ground Transportation Schedule/i.test(text)) return { code: "OO", force: true };
  if (/Frontier.*Ground Transportation Schedule/i.test(text)) return { code: "F9", force: true };
  if (/Spirit.*Ground Transportation Schedule/i.test(text)) return { code: "NK", force: true };
  if (/GoJet.*Ground Transportation Schedule/i.test(text)) return { code: "G7", force: true };
  if (/Aeromexico.*Ground Transportation Schedule/i.test(text)) return { code: "AM", force: true };
  return null;
}

async function extractRecords(arrayBuffer) {
  const doc = await pdfjs.getDocument({
    data: new Uint8Array(arrayBuffer),
    useSystemFonts: true,
  }).promise;
  const allRecords = [];
  // State persists across pages so date carry-forward works on multi-page PDFs.
  const state = { curDate: null };
  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p);
    const viewport = page.getViewport({ scale: 1 });
    const content = await page.getTextContent();
    const items = content.items
      .filter((i) => i.str && i.str.trim())
      .map((i) => {
        const m = pdfjs.Util.transform(viewport.transform, i.transform);
        return { str: i.str.trim(), x: m[4], y: m[5] };
      });
    // Format detection sticks across pages — Condor's "Overnight list"
    // banner and "Airports: LAX" parameter only appear on page 1, but later
    // pages still contain Duty data rows that need the same parser.
    let fmt = detectFormat(items);
    if (fmt) state.fmt = fmt;
    else fmt = state.fmt;
    // Airline-owner banner is only on page 1 too — remember it so page-2+
    // rows still get the right client attribution.
    const src = detectSourceAirline(items);
    if (src) state.sourceAirline = src;
    let pageRecs = [];
    if (fmt === "gojet") pageRecs = parseGoJetPage(items);
    else if (fmt === "condor") pageRecs = parseCondorPage(items, state);
    else if (fmt === "standard") pageRecs = parsePage(items, state);
    if (state.sourceAirline) {
      for (const r of pageRecs) r.sourceAirline = state.sourceAirline;
    }
    allRecords.push(...pageRecs);
  }
  return allRecords;
}

// ---------------------------------------------------------------------------
// GoJet weekly-calendar parser. Each PDF is one month per page laid out as a
// 7-column calendar. For each day cell we extract the "Pick-up from Airport"
// (ARRIVE) lines and the "Drop-off at Airport" (DEPART) lines, then convert
// each line into a {date, passengers, airport, from, pickupTime, to} record
// that mimics the Group 1 shape so recordToTransformed() can reuse its logic.
// ---------------------------------------------------------------------------
function findGoJetHeader(rows) {
  let airport = null, hotel = null, year = null, monthIdx = null;
  for (const row of rows) {
    for (const it of row) {
      const m = it.str.match(GOJET_HEADER_RE);
      if (m && !airport) { airport = m[1]; hotel = m[2]; }
      const mm = it.str.match(GOJET_MONTH_RE);
      if (mm && monthIdx === null) {
        monthIdx = MONTHS_LONG.indexOf(mm[1]);
        year = parseInt(mm[2], 10);
      }
    }
  }
  return { airport, hotel, year, monthIdx };
}

function findGoJetColumnXs(rows) {
  for (const row of rows) {
    const pickups = row.filter((it) => it.str === "Pick-up from Airport");
    if (pickups.length >= 5) {
      return pickups.map((it) => it.x).sort((a, b) => a - b);
    }
  }
  return null;
}

function nearestColIdx(x, colXs) {
  let best = 0, bestD = Infinity;
  for (let i = 0; i < colXs.length; i++) {
    const d = Math.abs(x - colXs[i]);
    if (d < bestD) { bestD = d; best = i; }
  }
  return best;
}

function readGoJetDayRow(row, colXs) {
  const days = new Array(colXs.length).fill(null);
  let hits = 0;
  for (const it of row) {
    if (!GOJET_DAY_RE.test(it.str)) continue;
    const ci = nearestColIdx(it.x, colXs);
    if (days[ci] == null) { days[ci] = parseInt(it.str, 10); hits++; }
  }
  return hits >= 5 ? days : null;
}

// Walk every day-number row in order and assign actual (year, month, day)
// using a running counter. Crosses month boundaries whenever the day number
// resets to a lower value. Handles trailing days of prev month at the top of
// the calendar and leading days of next month at the bottom.
function assignGoJetDates(allDayRows, baseMonthIdx, baseYear) {
  let monthIdx = baseMonthIdx;
  let year = baseYear;
  const firstDay = allDayRows[0]?.find((d) => d != null);
  if (firstDay != null && firstDay > 1) {
    monthIdx -= 1;
    if (monthIdx < 0) { monthIdx = 11; year -= 1; }
  }
  let lastDay = 0;
  const out = [];
  for (const days of allDayRows) {
    const assigned = new Array(days.length).fill(null);
    for (let i = 0; i < days.length; i++) {
      const d = days[i];
      if (d == null) continue;
      if (d < lastDay) {
        monthIdx += 1;
        if (monthIdx > 11) { monthIdx = 0; year += 1; }
      }
      assigned[i] = { year, monthIdx, day: d };
      lastDay = d;
    }
    out.push(assigned);
  }
  return out;
}

function parseGoJetPage(items) {
  const rows = rowsOf(items);
  const header = findGoJetHeader(rows);
  const colXs = findGoJetColumnXs(rows);
  if (!colXs || header.monthIdx == null || !header.airport) return [];

  // Resolve calendar dates per (week-row, column).
  const dayRowsRaw = [];
  for (const row of rows) {
    const days = readGoJetDayRow(row, colXs);
    if (days) dayRowsRaw.push({ row, days });
  }
  const dateRows = assignGoJetDates(
    dayRowsRaw.map((d) => d.days),
    header.monthIdx,
    header.year
  );
  const dateByRowRef = new Map();
  for (let i = 0; i < dayRowsRaw.length; i++) dateByRowRef.set(dayRowsRaw[i].row, dateRows[i]);

  // Index section boundaries (Pick-up vs Drop-off) per column so each line
  // gets the right type. Records before "Drop-off at Airport" in their column
  // are ARRIVE; after are DEPART.
  const sectionMarkers = [];
  for (const row of rows) {
    for (const it of row) {
      if (it.str === "Pick-up from Airport") sectionMarkers.push({ y: it.y, ci: nearestColIdx(it.x, colXs), kind: "ARRIVE" });
      else if (it.str === "Drop-off at Airport") sectionMarkers.push({ y: it.y, ci: nearestColIdx(it.x, colXs), kind: "DEPART" });
    }
  }
  function sectionAt(ci, y) {
    let last = null;
    for (const m of sectionMarkers) {
      if (m.ci !== ci) continue;
      if (m.y < y && (!last || m.y > last.y)) last = m;
    }
    return last ? last.kind : "ARRIVE";
  }

  // Build Group-1-shaped records so recordToTransformed handles them.
  const out = [];
  let curDates = null;
  for (const row of rows) {
    if (dateByRowRef.has(row)) { curDates = dateByRowRef.get(row); continue; }
    if (!curDates) continue;
    for (const it of row) {
      const m = it.str.match(GOJET_LINE_RE);
      if (!m) continue;
      const ci = nearestColIdx(it.x, colXs);
      const d = curDates[ci];
      if (!d) continue;
      const [, hh, mm, flight, riders, airline] = m;
      const carrier = airline || "UA";
      // Format DDMMM / "DD MMM HH:mm" / "DD-MMM-YYYY" to feed into the existing
      // transform helpers that already know how to handle these.
      const dd = String(d.day).padStart(2, "0");
      const mon = MONTH3_LIST[d.monthIdx];
      const dateStr = `${dd}-${mon}-${d.year}`;
      const time = `${hh.padStart(2, "0")}:${mm}`;
      const flightStr = `${carrier} ${flight}-${dd} ${mon} ${time}`;
      const pickupTime = `${dd} ${mon} ${time}`;
      const type = sectionAt(ci, it.y);
      if (type === "ARRIVE") {
        out.push({
          date: dateStr,
          passengers: String(riders),
          airport: header.airport,
          from: flightStr,        // airport-side: "UA 4464-31 May 13:06"
          pickupTime,
          to: header.hotel,
        });
      } else {
        out.push({
          date: dateStr,
          passengers: String(riders),
          airport: header.airport,
          from: header.hotel,
          pickupTime,
          to: flightStr,
        });
      }
    }
  }
  return out;
}

// Per-airport vehicle + default rates lookup — mirrors the switch in
// ImportData.js so PDF imports get the same vehicle/rate semantics as xls.
function applyAirportDefaults(transformed, airportCode, clientUpper) {
  switch (airportCode) {
    case "LAX":
      transformed.vehicle = "1002";
      if (clientUpper === "B6") transformed.rate = 62;
      if (clientUpper === "QX") transformed.rate = 65;
      if (clientUpper === "AS") transformed.rate = 65;
      // Condor: rate left at 0; manager can override per-trip until correct.
      if (clientUpper === "DE") transformed.rate = 0;
      break;
    case "BUR":
      transformed.vehicle = "1002";
      if (clientUpper === "B6") transformed.rate = 44;
      break;
    case "SDF":
      transformed.vehicle = "2200";
      if (clientUpper === "WN") transformed.rate = 73;
      break;
    case "LAS":
      transformed.vehicle = "1701";
      if (clientUpper === "B6") transformed.rate = 39.43;
      if (clientUpper === "RV") transformed.rate = 38;
      if (clientUpper === "AM") transformed.rate = 54;
      if (clientUpper === "AC") transformed.rate = 38;
      // Condor: rate left at 0; manager can override per-trip until correct.
      if (clientUpper === "DE") transformed.rate = 0;
      break;
    case "MCO":
      transformed.vehicle = "1500";
      if (clientUpper === "B6") transformed.rate = 40.8;
      if (clientUpper === "WN") transformed.rate = 60;
      break;
    case "MSP":
      transformed.vehicle = "2001";
      if (clientUpper === "WN") transformed.rate = 70;
      break;
    case "CVG":
      transformed.vehicle = "2100";
      if (clientUpper === "WN") transformed.rate = 95;
      break;
    case "PHL":
      transformed.vehicle = "1201";
      if (clientUpper === "F9") transformed.rate = 48;
      break;
    case "DAY":
      transformed.vehicle = "601";
      if (clientUpper === "ENDEAVOR") transformed.rate = 100;
      if (clientUpper === "9E") transformed.rate = 70;
      // GoJet operates United Express (UA) out of DAY.
      if (clientUpper === "UA") transformed.rate = 70;
      break;
    case "BUF":
      transformed.vehicle = "2300";
      transformed.rate = 70;
      break;
    case "RDU":
      transformed.vehicle = "2401";
      // Rate now lives in LocationAirlineRates (B6 @ RDU = $69); leave 0 here
      // so getDefaultRate() picks it up from the lookup at import time.
      break;
    default:
      transformed.vehicle = "DEFAULT";
      transformed.rate = 0;
  }
}

const _MONTHS_IDX = { Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11 };
function parsePickupTimeAirport(s, year, airportCode) {
  if (!s) return null;
  const m = String(s).trim().match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const mon = m[2][0].toUpperCase() + m[2].slice(1, 3).toLowerCase();
  const monthIdx = _MONTHS_IDX[mon];
  if (monthIdx == null) return null;
  return airportLocalToUtc(airportCode, year, monthIdx, parseInt(m[1],10), parseInt(m[3],10), parseInt(m[4],10));
}

function recordToTransformed(rec, errorData) {
  const scheduleDate = dayjs(`${rec.date}`, "DD-MMM-YYYY");
  const recAirportCode = (rec.airport || "").toUpperCase();
  const pickupDateTime = parsePickupTimeAirport(rec.pickupTime, scheduleDate.year(), recAirportCode);

  const newItem = {
    scheduleDate,
    passenger: rec.passengers,
    airport: rec.airport,
    pickup: rec.from,
    dropoff: rec.to,
    pickupDateTime,
  };
  if (!passValidation(newItem)) errorData.push([rec.date, rec.airport, rec.from, rec.pickupTime, rec.to]);

  const transformed = {
    company: "ACT",
    status: "ACTIVE",
    pilots: 0,
    flightCrew: 0,
    code: "",
    rate: 0,
    airport: newItem.airport,
    passenger: newItem.passenger,
  };

  // Cross-station shuttle detection: BOTH the From and To columns are flight
  // strings AND the row has two different airport codes (e.g. JB MCO PDF
  // rows that read `MCO | B6 BASE-... | FLL | B6 1575-...`). In that shape
  // the crew is being driven from one airport to another, not to a hotel,
  // so both pickup and dropoff must be real airport Location codes.
  if (
    endsWithTime(newItem.pickup) &&
    endsWithTime(newItem.dropoff) &&
    rec.toAirport &&
    rec.toAirport !== rec.airport
  ) {
    // One side of the row is a positioning marker ("B6 BASE") and the other
    // is the actual crew flight. Prefer the real flight number for FlightNo
    // (the user wants to see e.g. "1575", not "BASE") but keep the airline
    // consistent — both sides use the same carrier.
    const pickupSplit = newItem.pickup.split("-");
    const dropSplit = newItem.dropoff.split("-");
    const pickupFlight = getFlightNoFromEntry(pickupSplit);
    const dropFlight = getFlightNoFromEntry(dropSplit);
    const preferDrop = /^\d+$/.test(dropFlight || "") || !/^\d+$/.test(pickupFlight || "");
    const flightSide = preferDrop ? dropSplit : pickupSplit;
    transformed.pickupLocation = newItem.airport;
    transformed.dropoffLocation = rec.toAirport;
    transformed.flightNo = getFlightNoFromEntry(flightSide);
    // Cross-station: the time on the drop side belongs to the destination
    // airport (rec.toAirport), so use its TZ for the local→UTC conversion.
    const t = getDropoffTimeFromEntry(dropSplit, scheduleDate.year(), rec.toAirport);
    transformed.scheduleDate = t || scheduleDate.toDate();
    transformed.pickupDateTime = newItem.pickupDateTime;
    transformed.pass = newItem.passenger;
    transformed.client = getAirlineFromEntry(flightSide);
    // Cross-station shuttles are billed by (from, to, airline) via
    // CrossStationRates and belong to the ORIGIN airport's manager (the
    // van is dispatched from there). Type "DEPART" so the origin
    // airport's crew QR page picks them up.
    transformed.type = "DEPART";
    transformed.isCrossStation = true;
  } else if (endsWithTime(newItem.pickup)) {
    const pickupSplit = newItem.pickup.split("-");
    transformed.dropoffLocation = newItem.dropoff;
    transformed.pickupLocation = newItem.airport;
    transformed.flightNo = getFlightNoFromEntry(pickupSplit);
    transformed.scheduleDate = getDropoffTimeFromEntry(pickupSplit, scheduleDate.year(), recAirportCode);
    transformed.pickupDateTime = newItem.pickupDateTime;
    transformed.pass = newItem.passenger;
    transformed.client = getAirlineFromEntry(pickupSplit);
    transformed.type = "ARRIVE";
  } else if (endsWithTime(newItem.dropoff)) {
    const dropoffSplit = newItem.dropoff.split("-");
    // Cross-station DEPART: hotel pickup in home airport region, flight
    // departs from a DIFFERENT airport (e.g. MCO PDF row
    // "Hilton Orlando | 16:06 | PBI | B6 0368"). The row's second Location
    // column names the flight's departure airport; use it as the actual
    // dropoff so the display and rate lookup reflect the true destination.
    const isCross = rec.toAirport && rec.toAirport !== rec.airport;
    transformed.dropoffLocation = isCross ? rec.toAirport : newItem.airport;
    transformed.pickupLocation = newItem.pickup;
    transformed.flightNo = getFlightNoFromEntry(dropoffSplit);
    // The flight's departure time lives in the destination airport's TZ
    // when this is a cross-station row; otherwise the row's home airport.
    const flightAirport = isCross ? rec.toAirport : recAirportCode;
    const t = getDropoffTimeFromEntry(dropoffSplit, scheduleDate.year(), flightAirport);
    transformed.scheduleDate = t || scheduleDate.toDate();
    transformed.pickupDateTime = newItem.pickupDateTime;
    transformed.pass = newItem.passenger;
    transformed.client = getAirlineFromEntry(dropoffSplit);
    transformed.type = "DEPART";
    if (isCross) transformed.isCrossStation = true;
  } else if (
    rec.toAirport &&
    rec.toAirport !== rec.airport &&
    newItem.pickup && newItem.dropoff
  ) {
    // Cross-station hotel-to-hotel: neither side has a flight time (both
    // sides are hotel names) but the two Location columns name different
    // airports. Example MCO PDF row:
    //   MCO | SpringHill Lake Nona | 13:00 | FLL | Hilton Fort Lauderdale
    // Model as a DEPART priced via CrossStationRates: pickup hotel →
    // destination airport for rate lookup, and dropoff hotel for display.
    transformed.pickupLocation = newItem.pickup;
    transformed.dropoffLocation = newItem.dropoff;
    transformed.flightNo = "";
    transformed.scheduleDate = newItem.pickupDateTime || scheduleDate.toDate();
    transformed.pickupDateTime = newItem.pickupDateTime;
    transformed.pass = newItem.passenger;
    transformed.client = rec.sourceAirline?.code || "";
    transformed.type = "DEPART";
    transformed.isCrossStation = true;
    // Downstream rate lookup uses airportCode → destination-airport CSR
    // (see the isCrossStation fallback in ImportData.js), so the seeded
    // MCO→FLL rate applies even though the puID is a hotel.
    transformed._crossStationToAirport = rec.toAirport;
  } else {
    return null;
  }

  // If the sheet header identified a client airline with force=true
  // (e.g. Horizon Air, where AS/DHA flight prefixes are billing curiosities),
  // override the row's own client. force=false sheets (JetBlue) only fill
  // in when the hotel-hotel branch above couldn't infer one.
  if (rec.sourceAirline?.force) transformed.client = rec.sourceAirline.code;

  const airportCode = (newItem.airport || "").toUpperCase();
  transformed.airportCode = airportCode;
  applyAirportDefaults(transformed, airportCode, (transformed.client || "").toUpperCase());

  // Last-line defense: refuse to emit a row whose ScheduleDate didn't
  // parse. The xls/PDF parsers SHOULD never get here with a bad date, but
  // a malformed row in a future PDF template could silently feed an
  // "Invalid Date" object downstream and crash the importer.
  const t = transformed.scheduleDate instanceof Date
    ? transformed.scheduleDate.getTime()
    : new Date(transformed.scheduleDate).getTime();
  if (!Number.isFinite(t)) {
    console.warn("PDF row dropped — invalid ScheduleDate:", rec);
    errorData.push([rec.date, rec.airport, rec.from, rec.pickupTime, rec.to]);
    return null;
  }

  return transformed;
}

// ---------------------------------------------------------------------------
// Condor "RP-ReportPortal — Overnight list" parser. Each row is one flight
// leg; pairs of consecutive rows are an overnight stay. We emit one record
// per leg: arrival legs → ARRIVE (airport→hotel) and departure legs →
// DEPART (hotel→airport). Hotel is hard-coded per station because the PDF
// doesn't carry it (see CONDOR_HOTELS above — change those strings if a
// station's hotel changes).
// ---------------------------------------------------------------------------
function findCondorTargetAirport(rows) {
  for (const row of rows) {
    const strs = row.map((r) => r.str);
    const idx = strs.indexOf("Airports:");
    if (idx === -1) continue;
    for (let k = idx + 1; k < strs.length; k++) {
      if (AIRPORT_RE.test(strs[k])) return strs[k];
    }
  }
  return null;
}

function condorDateParts(s) {
  const m = s.match(CONDOR_DATE_RE);
  if (!m) return null;
  const day = parseInt(m[1], 10);
  const monthIdx = parseInt(m[2], 10) - 1;
  const yy = parseInt(m[3], 10);
  const year = yy < 70 ? 2000 + yy : 1900 + yy;
  return { day, monthIdx, year };
}

function parseCondorDataRow(row) {
  const strs = row.map((r) => r.str);
  if (strs[0] !== "Duty") return null;
  const fm = (strs[1] || "").match(CONDOR_FLIGHT_RE);
  if (!fm) return null;
  const airline = fm[1];
  const flightNo = fm[2];

  const dates = strs.filter((s) => CONDOR_DATE_RE.test(s));
  const times = strs.filter((s) => CONDOR_HHMM_RE.test(s));
  const airports = strs.filter((s) => AIRPORT_RE.test(s));
  if (dates.length < 2 || times.length < 2 || airports.length < 2) return null;

  // PDF order: Dep, DepDate, DepTime, ArrTime, ArrDate, Arr
  const depAirport = airports[0];
  const arrAirport = airports[1];
  const depDate = dates[0];
  const arrDate = dates[1];
  const depTime = times[0];
  const arrTime = times[1];

  // "Total" = sum of CP/FO/SF/PU/FB. Right-most integer; "Stay" (if present)
  // is HH:MM, not a digit-only string.
  const ints = strs.filter((s) => /^\d+$/.test(s));
  const total = ints.length ? parseInt(ints[ints.length - 1], 10) : 1;

  return { airline, flightNo, depAirport, depDate, depTime, arrAirport, arrDate, arrTime, total };
}

function buildCondorLegRecord(leg, type, target, hotel) {
  const parts = type === "ARRIVE" ? condorDateParts(leg.arrDate) : condorDateParts(leg.depDate);
  if (!parts) return null;
  const time = type === "ARRIVE" ? leg.arrTime : leg.depTime;
  const mon = MONTH3_LIST[parts.monthIdx];
  const dd = String(parts.day).padStart(2, "0");
  const flightStr = `${leg.airline} ${leg.flightNo}-${dd} ${mon} ${time}`;
  const pickupTime = `${dd} ${mon} ${time}`;
  const dateStr = `${dd}-${mon}-${parts.year}`;
  const passengers = String(leg.total || 1);
  if (type === "ARRIVE") {
    return { date: dateStr, passengers, airport: target, from: flightStr, pickupTime, to: hotel };
  }
  return     { date: dateStr, passengers, airport: target, from: hotel, pickupTime, to: flightStr };
}

function parseCondorPage(items, state) {
  const rows = rowsOf(items);
  // The "Airports: LAX" parameter only appears on page 1 of the Condor
  // RP-ReportPortal export; pages 2+ have no header block. Cache the
  // resolved airport on the shared state so subsequent pages still parse.
  let target = findCondorTargetAirport(rows);
  if (target) state.condorTarget = target;
  else target = state.condorTarget;
  if (!target) return [];
  const hotel = CONDOR_HOTELS[target];
  if (!hotel) {
    // Unknown station — leave a console hint so the operator notices.
    console.warn(`Condor PDF references unknown station "${target}" — add a hotel mapping in pdfSchedule.js CONDOR_HOTELS to enable import.`);
    return [];
  }

  const out = [];
  for (const row of rows) {
    const leg = parseCondorDataRow(row);
    if (!leg) continue;
    if (leg.arrAirport === target) {
      const r = buildCondorLegRecord(leg, "ARRIVE", target, hotel);
      if (r) out.push(r);
    } else if (leg.depAirport === target) {
      const r = buildCondorLegRecord(leg, "DEPART", target, hotel);
      if (r) out.push(r);
    }
  }
  return out;
}

// Pick the "home" airport for a PDF — the majority airport code across
// its rows. This is the operational base for the schedule: e.g. a JB MCO
// PDF has 30+ rows at MCO but a handful at TPA/PBI (positioning legs),
// yet the MCO manager owns every trip in the file. The importer uses this
// to override per-row manager lookup when a row's own airport doesn't
// have a manager assigned.
function homeAirportOf(records) {
  const tally = {};
  for (const r of records) {
    const code = (r?.airport || "").toUpperCase();
    if (!code) continue;
    tally[code] = (tally[code] || 0) + 1;
  }
  let best = null, bestN = 0;
  for (const [code, n] of Object.entries(tally)) {
    if (n > bestN) { best = code; bestN = n; }
  }
  return best;
}

export async function parsePdfFile(file) {
  try {
    const buf = await file.arrayBuffer();
    const records = await extractRecords(buf);
    if (records.length === 0) {
      toast.error(`No schedule rows found in ${file.name} — is this the expected format?`);
      return { jsonData: [], errorData: [] };
    }
    const homeAirport = homeAirportOf(records);
    const errorData = [];
    const jsonData = [];
    for (const rec of records) {
      try {
        const t = recordToTransformed(rec, errorData);
        if (t) {
          // Tag every row with the PDF's operational home so the importer
          // can fall back to the home-airport manager when the row's own
          // airport has none.
          t.homeAirport = homeAirport;
          jsonData.push(t);
        }
      } catch (_e) {
        errorData.push([rec.date, rec.airport, rec.from, rec.pickupTime, rec.to]);
      }
    }
    return { jsonData, errorData };
  } catch (err) {
    console.error(`Error reading PDF ${file.name}`, err);
    toast.error(`Error while reading ${file.name}`);
    return { jsonData: [], errorData: [] };
  }
}
